import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { markTooltipSeen } from "../redux/actions/authActions";

const SPOTLIGHT_PADDING = 10;
const TOOLTIP_WIDTH = 360;
const VIEWPORT_MARGIN = 16;

const getTooltipPosition = (rect, placement = "bottom") => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const centeredLeft = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
  const safeCenteredLeft = Math.min(
    Math.max(VIEWPORT_MARGIN, centeredLeft),
    viewportWidth - TOOLTIP_WIDTH - VIEWPORT_MARGIN,
  );
  const safeRightLeft = Math.min(
    Math.max(VIEWPORT_MARGIN, rect.right + 16),
    viewportWidth - TOOLTIP_WIDTH - VIEWPORT_MARGIN,
  );
  const safeLeftLeft = Math.max(
    VIEWPORT_MARGIN,
    rect.left - TOOLTIP_WIDTH - 16,
  );
  const spaceRight = viewportWidth - rect.right;
  const spaceLeft = rect.left;
  const defaultTop = Math.min(
    Math.max(VIEWPORT_MARGIN, rect.bottom + 16),
    viewportHeight - 180,
  );
  const topAbove = Math.max(VIEWPORT_MARGIN, rect.top - 170);
  const safeTop = Math.min(
    Math.max(VIEWPORT_MARGIN, rect.top),
    viewportHeight - 180,
  );

  const clampTop = (top) =>
    Math.min(Math.max(VIEWPORT_MARGIN, top), viewportHeight - 180);

  const rectsOverlap = (a, b) =>
    !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom);

  const getTooltipRect = (left, top) => {
    const clampedLeft = Math.min(
      Math.max(VIEWPORT_MARGIN, left),
      viewportWidth - TOOLTIP_WIDTH - VIEWPORT_MARGIN,
    );
    const clampedTop = clampTop(top);

    return {
      left: clampedLeft,
      top: clampedTop,
      right: clampedLeft + TOOLTIP_WIDTH,
      bottom: clampedTop + 180,
    };
  };

  const targetRect = {
    left: rect.left - SPOTLIGHT_PADDING,
    top: rect.top - SPOTLIGHT_PADDING,
    right: rect.right + SPOTLIGHT_PADDING,
    bottom: rect.bottom + SPOTLIGHT_PADDING,
  };

  const pickNonOverlapping = (candidates) => {
    for (const candidate of candidates) {
      const tooltipRect = getTooltipRect(candidate.left, candidate.top);
      if (!rectsOverlap(tooltipRect, targetRect)) {
        return { left: tooltipRect.left, top: tooltipRect.top };
      }
    }
    const fallback = getTooltipRect(candidates[0].left, candidates[0].top);
    return { left: fallback.left, top: fallback.top };
  };

  switch (placement) {
    case "top":
      return pickNonOverlapping([
        { left: safeCenteredLeft, top: topAbove },
        { left: safeCenteredLeft, top: rect.bottom + 16 },
        { left: safeCenteredLeft, top: defaultTop },
      ]);
    case "left":
      // Prefer showing on the right (for "Add" buttons) to avoid covering the CTA.
      // If no space on the right, fall back to left, then to centered.
      return pickNonOverlapping([
        { left: rect.right + 16, top: safeTop },
        { left: rect.left - TOOLTIP_WIDTH - 16, top: safeTop },
        { left: safeCenteredLeft, top: rect.bottom + 16 },
        { left: safeCenteredLeft, top: topAbove },
      ]);
    case "right":
      return pickNonOverlapping([
        { left: rect.right + 16, top: safeTop },
        { left: rect.left - TOOLTIP_WIDTH - 16, top: safeTop },
        { left: safeCenteredLeft, top: rect.bottom + 16 },
        { left: safeCenteredLeft, top: topAbove },
      ]);
    default: {
      const spaceBelow = viewportHeight - rect.bottom;
      const showAbove = spaceBelow < 180 && rect.top > 200;

      return pickNonOverlapping([
        { left: safeCenteredLeft, top: showAbove ? topAbove : defaultTop },
        { left: rect.right + 16, top: safeTop },
        { left: rect.left - TOOLTIP_WIDTH - 16, top: safeTop },
        { left: safeCenteredLeft, top: rect.bottom + 16 },
      ]);
    }
  }
};

export default function GuidedTour({
  steps,
  run,
  onComplete,
  onSkip,
  markSeenApi = true,
  persistKey,
  lockScroll = false,
}) {
  const dispatch = useDispatch();
  const tooltipSeen = useSelector((state) => state?.authReducer?.tooltipSeen);
  const [stepIndex, setStepIndex] = useState(0);
  const [anchorRect, setAnchorRect] = useState(null);
  const [spotlightRect, setSpotlightRect] = useState(null);
  const autoSidebarToggleAttemptedRef = React.useRef(false);
  const hasMarkedSeenRef = React.useRef(false);
  const hasLoadedPersistedStepRef = React.useRef(false);

  const readPersistedStep = React.useCallback(() => {
    if (!persistKey) return 0;
    try {
      if (typeof window === "undefined" || !window.sessionStorage) return 0;
      const raw = window.sessionStorage.getItem(persistKey);
      const parsed = Number(raw);
      if (!Number.isFinite(parsed)) return 0;
      if (parsed < 0) return 0;
      if (Array.isArray(steps) && steps.length && parsed > steps.length - 1) return 0;
      return parsed;
    } catch (_e) {
      return 0;
    }
  }, [persistKey, steps]);

  const writePersistedStep = React.useCallback(
    (nextStepIndex) => {
      if (!persistKey) return;
      try {
        if (typeof window === "undefined" || !window.sessionStorage) return;
        window.sessionStorage.setItem(persistKey, String(nextStepIndex));
      } catch (_e) {
        // ignore
      }
    },
    [persistKey],
  );

  const clearPersistedStep = React.useCallback(() => {
    if (!persistKey) return;
    try {
      if (typeof window === "undefined" || !window.sessionStorage) return;
      window.sessionStorage.removeItem(persistKey);
    } catch (_e) {
      // ignore
    }
  }, [persistKey]);

  const activeStep = useMemo(
    () => (run ? steps?.[stepIndex] : null),
    [run, stepIndex, steps],
  );
  const highlightRect = useMemo(() => {
    if (!spotlightRect) {
      return null;
    }

    return {
      top: Math.max(0, spotlightRect.top - SPOTLIGHT_PADDING),
      left: Math.max(0, spotlightRect.left - SPOTLIGHT_PADDING),
      width: spotlightRect.width + SPOTLIGHT_PADDING * 2,
      height: spotlightRect.height + SPOTLIGHT_PADDING * 2,
    };
  }, [spotlightRect]);

  useEffect(() => {
    if (!run) {
      setStepIndex(0);
    }
  }, [run]);

  useEffect(() => {
    if (!run || !persistKey) {
      hasLoadedPersistedStepRef.current = false;
      return;
    }

    if (hasLoadedPersistedStepRef.current) return;
    hasLoadedPersistedStepRef.current = true;
    setStepIndex(readPersistedStep());
  }, [persistKey, readPersistedStep, run]);

  useEffect(() => {
    if (run) {
      hasMarkedSeenRef.current = false;
    }
  }, [run]);

  useEffect(() => {
    if (!run) return undefined;

    const isInsideTooltip = (node) => {
      if (!node) return false;
      if (node.closest) return !!node.closest(".tour-tooltip");
      return false;
    };

    const blockOutsideClick = (event) => {
      // Allow programmatic clicks (used by tour onNext helpers to open menus/modals).
      if (event && event.isTrusted === false) return;
      if (isInsideTooltip(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
    };

    // Prevent clicking anything except the tooltip buttons while tour is running.
    // Keep scroll working (wheel/scroll/keyboard scrolling is not blocked).
    document.addEventListener("pointerdown", blockOutsideClick, true);
    document.addEventListener("click", blockOutsideClick, true);

    return () => {
      document.removeEventListener("pointerdown", blockOutsideClick, true);
      document.removeEventListener("click", blockOutsideClick, true);
    };
  }, [run]);

  useEffect(() => {
    if (!run || !lockScroll) return undefined;

    const body = document?.body;
    if (!body) return undefined;

    const previousOverflow = body.style.overflow;
    body.style.overflow = "hidden";

    const prevent = (event) => {
      event.preventDefault();
    };

    const preventKeys = (event) => {
      const keys = [
        "ArrowUp",
        "ArrowDown",
        "PageUp",
        "PageDown",
        "Home",
        "End",
        " ",
        "Spacebar",
      ];
      if (keys.includes(event.key)) {
        event.preventDefault();
      }
    };

    window.addEventListener("wheel", prevent, { passive: false });
    window.addEventListener("touchmove", prevent, { passive: false });
    window.addEventListener("keydown", preventKeys, { passive: false });

    return () => {
      body.style.overflow = previousOverflow;
      window.removeEventListener("wheel", prevent);
      window.removeEventListener("touchmove", prevent);
      window.removeEventListener("keydown", preventKeys);
    };
  }, [lockScroll, run]);

  useLayoutEffect(() => {
    if (!run || !activeStep?.target) {
      setAnchorRect(null);
      setSpotlightRect(null);
      return undefined;
    }

    autoSidebarToggleAttemptedRef.current = false;

    const updatePosition = (options = {}) => {
      const { scrollIntoView: shouldScrollIntoView = false } = options;
      const element = document.querySelector(activeStep.target);

      if (!element) {
        const isMobile = window.innerWidth < 992;
        if (isMobile && !autoSidebarToggleAttemptedRef.current) {
          const toggleButton = document.querySelector(".ct_toggle_side");
          if (toggleButton) {
            autoSidebarToggleAttemptedRef.current = true;
            toggleButton.click();
            setTimeout(updatePosition, 350);
            return;
          }
        }
        setAnchorRect(null);
        setSpotlightRect(null);
        return;
      }

      if (shouldScrollIntoView) {
        const scrollTargetSelector =
          typeof activeStep.scrollTarget === "string"
            ? activeStep.scrollTarget
            : typeof activeStep.spotlightTarget === "string"
              ? activeStep.spotlightTarget
              : activeStep.target;
        const scrollTargetElement = document.querySelector(scrollTargetSelector) || element;

        scrollTargetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }

      const rect = element.getBoundingClientRect();

      const isOffscreenHorizontally =
        rect.right <= VIEWPORT_MARGIN ||
        rect.left >= window.innerWidth - VIEWPORT_MARGIN;

      if (isOffscreenHorizontally) {
        const isMobile = window.innerWidth < 992;
        if (isMobile && !autoSidebarToggleAttemptedRef.current) {
          const toggleButton = document.querySelector(".ct_toggle_side");
          if (toggleButton) {
            autoSidebarToggleAttemptedRef.current = true;
            toggleButton.click();
            setTimeout(updatePosition, 350);
            return;
          }
        }
      }

      // const isEffectivelyHidden = rect.width < 1 || rect.height < 1;
      // if (isEffectivelyHidden) {
      //   const isMobile = window.innerWidth < 992;
      //   if (isMobile && !autoSidebarToggleAttemptedRef.current) {
      //     const toggleButton = document.querySelector(".ct_toggle_side");
      //     if (toggleButton) {
      //       autoSidebarToggleAttemptedRef.current = true;
      //       toggleButton.click();
      //       setTimeout(updatePosition, 350);
      //       return;
      //     }
      //   }
      // }

      const isEffectivelyHidden = rect.width < 1 || rect.height < 1;
if (isEffectivelyHidden) {
  const isMobile = window.innerWidth < 992;
  if (isMobile && !autoSidebarToggleAttemptedRef.current) {
    const toggleButton = document.querySelector(".ct_toggle_side");
    if (toggleButton) {
      autoSidebarToggleAttemptedRef.current = true;
      toggleButton.click();
      setTimeout(updatePosition, 350);
      return;
    }
  }
  // Non-mobile ke liye bhi retry karo
  setTimeout(() => updatePosition({ scrollIntoView: true }), 400);
  return;
}

      setAnchorRect(rect);

      if (activeStep.spotlightTarget === "viewport") {
        setSpotlightRect({
          top: 0,
          left: 0,
          width: window.innerWidth,
          height: window.innerHeight,
        });
        return;
      }

      if (typeof activeStep.spotlightTarget === "string") {
        const spotlightEl = document.querySelector(activeStep.spotlightTarget);
        setSpotlightRect(spotlightEl ? spotlightEl.getBoundingClientRect() : rect);
        return;
      }

      setSpotlightRect(rect);
    };

    updatePosition({ scrollIntoView: true });

    const handleResize = () => updatePosition({ scrollIntoView: false });
    const handleScroll = () => updatePosition({ scrollIntoView: false });

    window.addEventListener("resize", handleResize);
    if (!lockScroll) {
      window.addEventListener("scroll", handleScroll, true);
    }

    // const timer = setTimeout(() => updatePosition({ scrollIntoView: true }), 250);
    const timer = setTimeout(() => updatePosition({ scrollIntoView: true }), 600);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
      if (!lockScroll) {
        window.removeEventListener("scroll", handleScroll, true);
      }
    };
  }, [activeStep, lockScroll, run]);

  useEffect(() => {
    if (!run || !activeStep?.target || !activeStep?.advanceOnTargetClick) {
      return undefined;
    }

    const element = document.querySelector(activeStep.target);

    if (!element) {
      return undefined;
    }

    const handleTargetClick = () => {
      handleNext();
    };

    element.addEventListener("click", handleTargetClick);

    return () => {
      element.removeEventListener("click", handleTargetClick);
    };
  }, [activeStep, run]);

  if (!run || !activeStep || !anchorRect || !highlightRect) {
    return null;
  }

  const tooltipPosition = getTooltipPosition(
    anchorRect,
    activeStep.placement || "bottom",
  );
  const isLastStep = stepIndex === steps.length - 1;

  const markCompleted = (force = false) => {
    // localStorage bilkul nahi - sirf Redux/API
    // Skip should always end the tour, even if markSeenApi is false.
    const shouldMarkSeen = force ? tooltipSeen !== true : markSeenApi && tooltipSeen !== true;
    if (shouldMarkSeen && !hasMarkedSeenRef.current) {
      hasMarkedSeenRef.current = true;
      dispatch(markTooltipSeen({ payload: {}, isErrorToast: false }));
    }
    clearPersistedStep();
  };

  const handleSkip = () => {
    markCompleted(true);
    onSkip?.();
  };

  const advanceStep = () => {
    if (isLastStep) {
      markCompleted();
      onComplete?.();
      return;
    }

    setStepIndex((current) => {
      const next = current + 1;
      writePersistedStep(next);
      return next;
    });
  };

  const handleNext = () => {
    activeStep?.onNext?.();

    if (activeStep?.nextDelay) {
      setTimeout(advanceStep, activeStep.nextDelay);
      return;
    }

    advanceStep();
  };

  const overlayColor = activeStep.overlayColor || "rgba(0, 0, 0, 0.8)";
  const nextLabel = activeStep.nextLabel || "Next";
  const skipLabel = activeStep.skipLabel || "Skip";
  const showSkipButton = activeStep.showSkipButton !== false;

  const tourUi = (
    <>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: highlightRect.top,
          background: overlayColor,
          zIndex: 9999999998,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "fixed",
          top: highlightRect.top,
          left: 0,
          width: highlightRect.left,
          height: highlightRect.height,
          background: overlayColor,
          zIndex: 9999999998,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "fixed",
          top: highlightRect.top,
          left: highlightRect.left + highlightRect.width,
          right: 0,
          height: highlightRect.height,
          background: overlayColor,
          zIndex: 9999999998,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "fixed",
          top: highlightRect.top + highlightRect.height,
          left: 0,
          right: 0,
          bottom: 0,
          background: overlayColor,
          zIndex: 9999999998,
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "fixed",
          top: highlightRect.top,
          left: highlightRect.left,
          width: highlightRect.width,
          height: highlightRect.height,
          borderRadius: activeStep.spotlightRadius || 16,
          zIndex: 999999999889,
          pointerEvents: "none",
          transition: "all 0.2s ease",
        }}
      />

      <div
        className={`tour-tooltip ${activeStep.className || ""}`}
        style={{
          position: "fixed",
          top: activeStep.tooltipFixedBottom ? "auto" : tooltipPosition.top,
          left: activeStep.tooltipFixedBottom ? "50%" : tooltipPosition.left,
          bottom: activeStep.tooltipFixedBottom
            ? (typeof activeStep.tooltipBottomOffset === "number"
              ? activeStep.tooltipBottomOffset
              : 16)
            : "auto",
          transform: activeStep.tooltipFixedBottom ? "translateX(-50%)" : "none",
          width: activeStep.tooltipWidth || TOOLTIP_WIDTH,
          background: "#fff",
          borderRadius: 14,
          padding: 20,
          zIndex: 99999999988998,
          boxShadow: "0px 0px 25px rgba(0,0,0,0.09)",
        }}
      >
        {activeStep.title ? (
          <h4
            style={{
              margin: "0 0 8px",
              fontSize: 18,
              fontWeight: 500,
              color: "#000",
            }}
          >
            {activeStep.title}
          </h4>
        ) : null}

        <p
          style={{
            margin: 0,
            fontSize: 14,
            lineHeight: 1.6,
            color: "#aaa",
          }}
        >
          {activeStep.content}
        </p>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginTop: "8px",
            paddingTop: "8px",
            borderTop: "1px solid #e6e6e6",
          }}
        >
          {showSkipButton ? (
            <button
              type="button"
              onClick={handleSkip}
              style={{
                border: "none",
                background: "transparent",
                color: "#000",
                borderRadius: 10,
                fontWeight: 500,
                padding: 0,
                padding: "4px 18px",
                border: "1px solid #000",
              }}
            >
              {skipLabel}
            </button>
          ) : (
            <span />
          )}

          <button
            type="button"
            onClick={handleNext}
            style={{
              border: "none",
              borderRadius: 10,
              background: "#013760",
              color: "#fff",
              fontWeight: 500,
              padding: "5px 18px",
            }}
          >
            {nextLabel}
          </button>
        </div>
      </div>
    </>
  );

  return typeof document !== "undefined" ? createPortal(tourUi, document.body) : tourUi;
}

